import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Text, Inputs, Buttons, Spinner, FlexContainer } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import EventService from 'Services/events';
import StorageService from 'Services/storage';
import { history } from 'Store';
import { routeStateSelector } from 'Store/modules/router/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import {
  useFetchGetFeatureFlag,
  useFetchSatUserStage,
  useRequestPreviousYears,
} from 'Containers/Datagathering/hooks';
import DgHeader from 'Containers/Datagathering/components/DgHeader';
import DgSlider from 'Containers/Datagathering/components/DgSlider';
import DgQuestions from 'Containers/Datagathering/components/DgQuestions';
import OneColLayout from 'Components/Layouts/OneColLayout';
import { routes } from 'Constants/routes';
import { validRfc } from 'Utils/validFields';
import { stages } from 'Constants/stages';
import { TAX_YEAR } from 'Config';
import * as Styled from './styles';

const initialState = {
  rfc: {
    value: '',
    isValid: true,
  },
};

const RelevantStages = [stages.DATA_RETRIEVAL, stages.DATA_PULLED, stages.DATA_PULLING];

const DgFirstStep = (): JSX.Element => {
  const [values, setValues] = useState(initialState);
  const isPreviousYears = useSelector(routeStateSelector);
  const { t } = useTranslation(locizeNS.DG);
  const { isLoading, data } = useFetchSatUserStage(isPreviousYears);
  const { isLoading: loading, data: featureFlag } = useFetchGetFeatureFlag();
  const { mutate: requestPreviousYears } = useRequestPreviousYears();

  const onValid = (value: string): boolean => {
    const isValidLength = value.length === 13;
    const isValid = isValidLength && validRfc(value);
    return isValid;
  };

  const handleOnChangeRfc = (value): void => {
    const rfcValue = value.replace(/[-\s]/g, '');
    setValues({
      rfc: { value, isValid: onValid(rfcValue) },
    });
  };
  useEffect(() => {
    if (
      data?.stage &&
      RelevantStages.includes(data?.stage?.value) &&
      data.stage.taxYear === TAX_YEAR &&
      !featureFlag?.flowActive
    ) {
      history.push(routes.PERSONAL_PHONE, {
        products: isPreviousYears?.products,
      });
    } else if (
      data?.stage &&
      RelevantStages.includes(data?.stage?.value) &&
      data.stage.taxYear === TAX_YEAR &&
      featureFlag?.flowActive
    ) {
      requestPreviousYears({});
      history.push(isPreviousYears?.products ? routes.PERSONAL_PHONE : routes.PERSONAL_PHONE_XML, {
        products: isPreviousYears?.products,
      });
    } else if (data?.rfc) {
      history.push(routes.DATAGATHERING_IDENTIFICATION_RFC, {
        products: isPreviousYears?.products,
        rfc: data.rfc || values.rfc.value || StorageService.getItem('rfc'),
      });
    }
  }, [data]);

  useEffect(() => {
    if (StorageService.getItem('rfc')) {
      const rfc = StorageService.getItem('rfc');
      handleOnChangeRfc(rfc);
    }
  }, [data]);

  const handleClick = (): void => {
    EventService.formSubmitted({
      screenId: 'dg-first-step',
      values: values.rfc.value.replace(/[-\s]/g, ''),
    });
    history.push(routes.DATAGATHERING_IDENTIFICATION_RFC, {
      products: isPreviousYears?.products,
      rfc: values.rfc.value.replace(/[-\s]/g, ''),
    });
  };

  if (isLoading || loading) {
    return (
      <Styled.SpinnerConteiner justify="center" align="center">
        <Spinner size="M" justify="center" align="center" />
      </Styled.SpinnerConteiner>
    );
  }
  return (
    <OneColLayout>
      <FlexContainer direction="column" align="center" justify="space-between">
        <Styled.ContentContainer fullWidth direction="column" align="center">
          <DgHeader />
          <Styled.Container direction="column" gap="dimension_24">
            <FlexContainer direction="column" gap="dimension_16">
              <Text type="headline" color="dark" size="S_M_M">
                {t('Dg.firstStep.title')}
              </Text>
              <Text type="paragraphs" color="grey" size="M_M_M">
                {t('Dg.firstStep.subtitle')}
              </Text>
            </FlexContainer>
            <Inputs.Text
              testID="rfc-input"
              label={t('Dg.firstStep.input.label')}
              type="text"
              placeholder="ABCD010101AA0"
              value={values.rfc.value}
              hasError={!values.rfc.isValid}
              errorMsg={t('Dg.rfc.error', {
                context: values.rfc.value.replace(/[-\s]/g, '').length === 12 && 'invalidLength',
              })}
              isMandatory
              onChange={val => handleOnChangeRfc(val.toUpperCase())}
            />
            <Buttons.Tiny
              label={t('Dg.firstStep.tiny')}
              onClick={() =>
                history.push(routes.DATAGATHERING_CALCULATE_RFC, {
                  products: isPreviousYears?.products,
                })
              }
            />
            <DgSlider />
            <DgQuestions />
          </Styled.Container>
        </Styled.ContentContainer>
        <Styled.ButtonContainer align="center" justify="center">
          <Buttons.Classic
            label={t('DGRfc.CTA')}
            mode="primary"
            onClick={handleClick}
            disabled={!values.rfc.value || !values.rfc.isValid}
            size="m"
          />
        </Styled.ButtonContainer>
      </FlexContainer>
    </OneColLayout>
  );
};

export default DgFirstStep;
